import { hierarchyApi } from '@/modules/hierarchy'
import { skipToken, useQueries, useQuery } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error-message'

export const userManagementEntityKeys = {
  root: ['user-management-entity'] as const,
  userManagementEntity: (entityId: string) =>
    [...userManagementEntityKeys.root, entityId] as const,
}

const retryUnlessApiError = (count: number, error: Error) =>
  !(error instanceof ApiError) && count < 2

export type UserManagementEntityState =
  | { status: 'loading' }
  | {
      status: 'ready'
      isUserManagementEntity: boolean
      userManagementEntityId: string | null
      userManagementEntityName: string | null
    }
  | { status: 'error'; message: string; retry: () => void }

// Resolves the user management entity that owns user management for an entity. An entity is
// itself a user management entity when the resolved id equals its own — the signal that
// gates the Access section (ADR-0037).
export function useUserManagementEntity(entityId: string | null): UserManagementEntityState {
  const query = useQuery({
    queryKey: userManagementEntityKeys.userManagementEntity(entityId ?? ''),
    queryFn: entityId === null ? skipToken : () => hierarchyApi.getUserManagementEntity(entityId),
    retry: retryUnlessApiError,
  })

  if (query.isSuccess)
    return {
      status: 'ready',
      isUserManagementEntity: query.data.userManagementEntityId === entityId,
      userManagementEntityId: query.data.userManagementEntityId,
      userManagementEntityName: query.data.userManagementEntityName,
    }
  if (query.isError)
    return {
      status: 'error',
      message: apiErrorMessage(
        query.error,
        'Couldn’t load access management for this entity. Try again.',
      ),
      retry: () => void query.refetch(),
    }
  return { status: 'loading' }
}

export function useIsUserManagementEntity(entityId: string | null): boolean {
  const userManagementEntity = useUserManagementEntity(entityId)
  return userManagementEntity.status === 'ready' && userManagementEntity.isUserManagementEntity
}

export type UserManagementEntityLookup = {
  userManagementEntityIds: ReadonlyMap<string, string>
  isPending: boolean
  error: Error | null
  retry: () => void
}

export function useUserManagementEntityIds(entityIds: string[]): UserManagementEntityLookup {
  const queries = useQueries({
    queries: entityIds.map((entityId) => ({
      queryKey: userManagementEntityKeys.userManagementEntity(entityId),
      queryFn: () => hierarchyApi.getUserManagementEntity(entityId),
      retry: retryUnlessApiError,
    })),
  })

  const userManagementEntityIds = new Map<string, string>()
  const failed: { refetch: () => unknown; error: Error }[] = []
  let isPending = false
  for (const [index, query] of queries.entries()) {
    const entityId = entityIds[index]
    if (entityId === undefined) continue
    if (query.isSuccess) {
      if (query.data.userManagementEntityId !== null)
        userManagementEntityIds.set(entityId, query.data.userManagementEntityId)
    } else if (query.isError) {
      failed.push({ refetch: query.refetch, error: query.error })
    } else {
      isPending = true
    }
  }

  return {
    userManagementEntityIds,
    isPending,
    error: failed[0]?.error ?? null,
    retry: () => failed.forEach((q) => void q.refetch()),
  }
}
